import { Delete, Edit, LockTwoTone, PeopleAltTwoTone } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ConfirmModal,
    IOrganisation,
    IParams,
    Page,
    PermissionKeys,
    Table,
    useGetOrganisations,
    useHasPermission,
    Widget,
} from '../../../shared';
import { DuplicatesWarning, EditOrganisationAccessDialog, SupportCaseTitle } from '../../components';
import { useGetSupportCase, useSaveSupportCase } from '../../hooks';

export function SupportCaseOrganisationsPage() {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;
    const navigate = useNavigate();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.SUPPORT_CASES_WRITE);
    const [editOrganisationAccessDialog, setEditOrganisationAccessDialog] = useState(false);

    const { data: supportCase } = useGetSupportCase(id);

    // get information of the selected organisations
    const { data: organisationsList } = useGetOrganisations({
        ids: supportCase?.organisations ? supportCase.organisations.map((org) => org.id as string) : [],
        pageSize: supportCase?.organisations?.length,
    });
    const organisations = organisationsList?.data || [];

    const { mutateAsync: saveSupportCase } = useSaveSupportCase();
    const removeOrganisationsAccess = async (id: string | undefined) => {
        if (supportCase) {
            supportCase.organisations = supportCase.organisations.filter((org) => org.id != id);
            await saveSupportCase(supportCase);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: t('nameLabel'),
            minWidth: 200,
            flex: 1,
            sortable: false,
        },
        ...(hasWritePermission
            ? ([
                  {
                      field: 'edit',
                      headerName: ' ',
                      sortable: false,
                      minWidth: 100,
                      align: 'right',
                      renderCell: ({ row: { name, id } }: GridCellParams<IOrganisation>) => {
                          return (
                              <>
                                  {organisations.length > 1 && (
                                      <ConfirmModal
                                          onConfirm={() => removeOrganisationsAccess(id)}
                                          button={
                                              <IconButton data-testid={`organisation-${id}-delete`}>
                                                  <Delete />
                                              </IconButton>
                                          }
                                          title={t('removeAccessTitle')}
                                          text={
                                              <>
                                                  <span style={{ fontWeight: 'bold' }}>{`${name} `}</span>
                                                  {t('removeAccessText')}
                                              </>
                                          }
                                      />
                                  )}
                              </>
                          );
                      },
                  },
              ] as GridColDef[])
            : ([] as GridColDef[])),
    ];

    return (
        <Page onBack={() => navigate(-1)} title={supportCase && <SupportCaseTitle supportCase={supportCase} />}>
            <Stack spacing={2}>
                <DuplicatesWarning supportCaseId={id} />
                <Widget
                    icon={
                        window.carity.environment.shareSupportCases ? (
                            <PeopleAltTwoTone color="primary" />
                        ) : (
                            <LockTwoTone color="primary" />
                        )
                    }
                    title={t(
                        window.carity.environment.shareSupportCases
                            ? 'organisationsWithOwnership'
                            : 'organisationsWithAccess',
                    )}
                    hasTable
                    actions={
                        hasWritePermission && (
                            <IconButton
                                onClick={() => setEditOrganisationAccessDialog(true)}
                                data-testid="edit-permissions-btn"
                            >
                                <Edit />
                            </IconButton>
                        )
                    }
                >
                    <Table rows={organisations} columns={columns} hideFooter paginationMode="client" />

                    <EditOrganisationAccessDialog
                        open={editOrganisationAccessDialog}
                        onClose={() => setEditOrganisationAccessDialog(false)}
                    />
                </Widget>
            </Stack>
        </Page>
    );
}
